import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { IconProps } from '@empiriecom/module-components/Icon/IconProps';
import Scroll from '@empiriecom/module-components/Scroll';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { FragmentConfiguration } from '@/config';
import { RatingIcons } from '@/src/components/icons';
import { useRating } from '../ProductContext';

const RatingStars = styled(RatingIcons)`
  width: 6.25rem;
  height: 1.25rem;
`;

const ReviewWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ReviewCount = styled.span`
  margin-left: 0.25rem;
  font-size: 0.875rem;
`;

const ScrollButton = styled.span<{ mobile: boolean }>`
  cursor: pointer;
  ${({ mobile }) => !mobile && 'margin-left: 0.5rem;'};
  font-size: 0.875rem;
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
`;

const ScrollButtonArrowWrapper = styled.span`
  margin-left: 0.25rem;
`;

const renderScrollLink = (
  scroll: () => void,
  avgRating: string,
  reviewCount: number,
  mobile: boolean,
  iconType: string,
  Arrow: React.FunctionComponent<IconProps>,
): JSX.Element =>
  mobile ? (
    <ScrollButton mobile={mobile} onClick={scroll}>
      <span data-testid="stars">
        <RatingStars maskWidth={`${parseFloat(avgRating) * 20}%`} iconType={iconType} />
      </span>
      <ReviewCount data-testid="review-count">({reviewCount})</ReviewCount>
    </ScrollButton>
  ) : (
    <>
      <span data-testid="stars">
        <RatingStars maskWidth={`${parseFloat(avgRating) * 20}%`} iconType={iconType} />
      </span>
      <ReviewCount data-testid="review-count">({reviewCount})</ReviewCount>
      <ScrollButton mobile={mobile} onClick={scroll} data-testid="scroll-desktop-label">
        <FormattedMessage id="ratings.label" defaultMessage="Bewertungen" />
        <ScrollButtonArrowWrapper>
          <Arrow size="0.875rem" />
        </ScrollButtonArrowWrapper>
      </ScrollButton>
    </>
  );

/**
 * <p>This is the review stars component. On load it makes a call against the ratings API for the given product via the `akl`.</p>
 * <p>If a response is received a scroll to link showing the average rating displayed via the rating stars icons as well as the
 * total ratings count will be rendered. On click this link scrolls to the element with the id `content`.</p>
 */
export const ReviewStars: FunctionComponent = () => {
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const config = useConfig<FragmentConfiguration>();
  const Arrow = getIcon('ArrowLargeRight');

  const { error, isLoading, rating } = useRating();

  return (
    <>
      {error && null}
      {(isLoading || !rating) && (
        <ReviewWrapper data-testid="review-stars-loading">
          <RatingStars maskWidth="0%" iconType={config.customerRatings.iconType} />
        </ReviewWrapper>
      )}
      {rating && (
        <ReviewWrapper data-testid="review-stars">
          <Scroll
            render={(scroll) =>
              renderScrollLink(
                scroll,
                rating.product.avgRating.toFixed(1),
                rating.product.reviewCount,
                mobile,
                config.customerRatings.iconType,
                Arrow,
              )
            }
            positionAfterScroll={config.customerRatings.enabled ? '#customer-ratings' : '#content'}
          />
        </ReviewWrapper>
      )}
    </>
  );
};

export default ReviewStars;
